// ============================================================================
// FSA CLASSIFICATION SERVICE
// ============================================================================

import { get, post, put } from '../../client/axiosClient';
import { fsaClassificationEndpoints } from '../../endpoints/fsa/fsaClassification.endpoints';
import { buildEndpointWithQuery } from '../../endpoints/base.endpoints';
import type { ApiResponse, ID, PaginatedResponse } from '../../types';
import type {
  CoverageSummary,
  AutoClassifySuggestion,
  ApplyAutoClassifyRequest,
  SetClassificationRequest,
  BulkClassifyRequest,
  BulkScheduleMapRequest,
  GroupClassifyRequest,
  GroupClassifyResult,
  GroupClassifyImpact,
  GroupClassifyPolicy,
} from '../../types/fsa/classification.types';
import type { TrialBalanceEntry, TrialBalanceEntryQuery } from '../../types/fsa/trialBalance.types';

class FsaClassificationService {
  async getEntries(tbId: ID, query?: TrialBalanceEntryQuery): Promise<PaginatedResponse<TrialBalanceEntry>> {
    const endpoint = query
      ? buildEndpointWithQuery(`/FsaClassifications/by-trial-balance/${tbId}`, query as Record<string, any>)
      : fsaClassificationEndpoints.getEntries(tbId);
    const response = await get<any>(endpoint);
    const raw = response.data;
    const paged = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: paged.items ?? [],
      pagination: {
        currentPage: paged.page ?? 1,
        pageSize: paged.pageSize ?? 20,
        totalPages: paged.totalPages ?? 0,
        totalItems: paged.totalCount ?? 0,
        hasNextPage: paged.hasNext ?? false,
        hasPreviousPage: paged.hasPrevious ?? false,
      },
    };
  }

  async getCoverage(tbId: ID): Promise<ApiResponse<CoverageSummary>> {
    const response = await get<ApiResponse<CoverageSummary>>(
      fsaClassificationEndpoints.getCoverage(tbId)
    );
    return response.data;
  }

  async getSuggestions(tbId: ID): Promise<ApiResponse<AutoClassifySuggestion[]>> {
    const response = await get<ApiResponse<AutoClassifySuggestion[]>>(
      fsaClassificationEndpoints.getSuggestions(tbId)
    );
    return response.data;
  }

  async applyAutoClassify(
    tbId: ID,
    request: ApplyAutoClassifyRequest
  ): Promise<ApiResponse<number>> {
    const response = await post<ApiResponse<number>>(
      fsaClassificationEndpoints.applyAutoClassify(tbId),
      request
    );
    return response.data;
  }

  async setClassification(
    entryId: ID,
    request: SetClassificationRequest
  ): Promise<ApiResponse<null>> {
    const response = await put<ApiResponse<null>>(
      fsaClassificationEndpoints.setClassification(entryId),
      request
    );
    return response.data;
  }

  /**
   * Bulk classify — assigns the same class to a list of entries. Powers
   * the multi-select mapping modal in the unmapped-ledger flow.
   */
  async bulkClassify(
    tbId: ID,
    request: BulkClassifyRequest
  ): Promise<ApiResponse<number>> {
    const response = await post<ApiResponse<number>>(
      fsaClassificationEndpoints.bulkClassify(tbId),
      request
    );
    return response.data;
  }

  /**
   * FSA-2.1.5: bulk schedule-only mapping. Maps a list of entries to a
   * single Note without picking a leaf class. The leaf is refined later
   * (via the bulk-classify modal or, in FSA-2.2, the Note Modal's
   * Pending Class Split section). `scheduleId = null` clears the mapping.
   */
  async bulkScheduleMap(
    tbId: ID,
    request: BulkScheduleMapRequest
  ): Promise<ApiResponse<number>> {
    const response = await post<ApiResponse<number>>(
      fsaClassificationEndpoints.bulkScheduleMap(tbId),
      request
    );
    return response.data;
  }

  /**
   * Clear every TB entry's `custom_class_local_id` reference to a given
   * engagement-time custom class. Called by the Note Modal's
   * `deleteCustomClass` flow so deletion doesn't leave dangling pointers
   * that resurrect the class on the next hydrate (and inflate the Note
   * total with an invisible orphan row). `schedule_id` on the affected
   * ledgers is preserved — they stay attached to the Note as
   * "schedule-only / pending class split", which the auditor can later
   * remap to a real class.
   */
  async clearCustomClassReferences(
    tbId: ID,
    localId: string,
  ): Promise<ApiResponse<number>> {
    const response = await post<ApiResponse<number>>(
      fsaClassificationEndpoints.clearCustomClassReferences(tbId, localId),
    );
    return response.data;
  }

  /**
   * FSA-2.1.8: pre-flight impact preview for group-classify. Returns the
   * classification-state breakdown of the group and the counts the chosen
   * policy will change vs skip. No DB writes.
   */
  async groupImpact(
    tbId: ID,
    groupName: string,
    policy?: GroupClassifyPolicy
  ): Promise<ApiResponse<GroupClassifyImpact>> {
    const endpoint = buildEndpointWithQuery(
      `/FsaClassifications/by-trial-balance/${tbId}/group-impact`,
      { groupName, policy }
    );
    const response = await get<ApiResponse<GroupClassifyImpact>>(endpoint);
    return response.data;
  }

  /**
   * FSA-2.1.8: writes a single classification target to every entry in a
   * parent_group, subject to the chosen policy. The target may be a template
   * leaf, an engagement custom class (customClassLocalId + scheduleId), or a
   * schedule-only mapping. Control accounts are always skipped. Manual
   * mappings are preserved unless policy = 'replace-all'.
   */
  async groupClassify(
    tbId: ID,
    request: GroupClassifyRequest
  ): Promise<ApiResponse<GroupClassifyResult>> {
    const response = await post<ApiResponse<GroupClassifyResult>>(
      fsaClassificationEndpoints.groupClassify(tbId),
      request
    );
    return response.data;
  }
}

export const fsaClassificationService = new FsaClassificationService();
export default fsaClassificationService;
